import cx from 'classnames';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { ExternalTripCountsData, VehicleClass, Settings } from '../../data/api';
import styles from '../../styles/metrics/MetricsCharts.module.scss';
import MetricChart, { ChartDataType } from './MetricChart';
import { MetricsData } from './use-metrics';
import { useLinearUnitsInfo } from '../../utils/use-linear-units';
import { convertChartDataForDisplay } from '../../utils/units-conversion';
import HighlightedMetric, {
    convertHighlightedMetricValuesForDisplay,
    highlightedMetricDisplayUnits,
} from './HighlightedMetric';

const CHART_TYPES: Record<
    ChartDataType,
    {
        key:
            | 'tripsPerDay'
            | 'tripDistancePerDay'
            | 'meanActiveVehiclesPerDay'
            | 'tripsPerVehiclePerDay';
        description: string;
        maximumFractionDigits: number;
    }
> = {
    tripsPerDay: {
        key: 'tripsPerDay',
        description: 'Number of trips on each day and the 14-day average.',
        maximumFractionDigits: 0,
    },
    tripDistancePerDay: {
        key: 'tripDistancePerDay',
        description:
            'Total distance of all trips on each day and the 14-day average.',
        maximumFractionDigits: 0,
    },
    tripsPerVehiclePerDay: {
        key: 'tripsPerVehiclePerDay',
        description:
            'Number of trips per active vehicle on each day and the 14-day average. This metric is calculated by dividing total daily trips by the average number of active vehicles on that day.',
        maximumFractionDigits: 2,
    },
    meanActiveVehiclesPerDay: {
        key: 'meanActiveVehiclesPerDay',
        description:
            'Average number of active vehicles per day and the 14-day average. Active vehicles refer to vehicles that are available for rent or rented.',
        maximumFractionDigits: 0,
    },
};

function useTimeRange(
    metrics: MetricsData,
    timeRange: string,
    externalTripCounts?: ExternalTripCountsData | null
) {
    return useMemo(() => {
        // Handle case where we're showind only data from external trips, so we have no data buckets to reference
        if (metrics.tripsPerDay.dataBuckets.length === 0) {
            return timeRange === 'all' ? 'All Data' : timeRange;
        }
        const firstDate = DateTime.fromISO(
            metrics.tripsPerDay.dataBuckets[0].xValue
        );
        const lastDate = DateTime.fromISO(
            metrics.tripsPerDay.dataBuckets[
                metrics.tripsPerDay.dataBuckets.length - 1
            ].xValue
        );
        const displayRange =
            timeRange === 'all'
                ? 'All Data'
                : `Q${firstDate.quarter} ${firstDate.year}`;
        return `${displayRange} | ${firstDate.toLocaleString()} - ${lastDate.toLocaleString()}`;
    }, [metrics.tripsPerDay.dataBuckets, timeRange]);
}

function useDisplayTimeRange(
    metrics: MetricsData,
    timeRange: string,
    externalTripCounts?: ExternalTripCountsData | null
) {
    return useMemo(() => {
        // Handle case where we're showind only data from external trips, so we have no data buckets to reference
        if (metrics.tripsPerDay.dataBuckets.length === 0) {
            return timeRange === 'all' ? 'All Data' : timeRange;
        }
        const firstDate = DateTime.fromISO(
            metrics.tripsPerDay.dataBuckets[0].xValue
        );
        const lastDate = DateTime.fromISO(
            metrics.tripsPerDay.dataBuckets[
                metrics.tripsPerDay.dataBuckets.length - 1
            ].xValue
        );
        const displayRange =
            timeRange === 'all'
                ? 'All Data'
                : `Q${firstDate.quarter} ${firstDate.year}`;
        return `${displayRange} | ${firstDate.toLocaleString()} - ${lastDate.toLocaleString()}`;
    }, [metrics.tripsPerDay.dataBuckets, timeRange]);
}

const VEHICLE_CLASS_STRINGS: Record<VehicleClass, string> = {
    all: 'All Vehicle',
    bike: 'Bike',
    car: 'Car',
    'e-bike': 'E-Bike',
    moped: 'Moped',
    scooter: 'Scooter',
    'seated-scooter': 'Seated Scooter',
    'delivery-robot': 'Delivery Robot',
    'cargo-bike': 'Cargo Bike',
    'cargo-e-bike': 'Cargo E-Bike',
    bus: 'Bus',
    truck: 'Truck',
    motorcycle: 'Motorcycle',
};

function composeName(
    chartType: ChartDataType,
    vehicleClass: VehicleClass
): string {
    let vcs = VEHICLE_CLASS_STRINGS[vehicleClass];
    switch (chartType) {
        case 'tripsPerDay':
            return `${vcs} Trips Per Day`;
        case 'tripDistancePerDay':
            return `${vcs} Trip Distance Per Day`;
        case 'tripsPerVehiclePerDay':
            vcs = vehicleClass === 'all' ? 'Vehicle' : vcs;
            return `Trips Per ${vcs} Per Day`;
        case 'meanActiveVehiclesPerDay':
            vcs = vehicleClass === 'all' ? 'Vehicle' : vcs;
            return `Average Active ${vcs}s Per Day`;
        default:
            throw new Error(`No name available for ${chartType}`);
    }
}

interface MetricsChartsProps {
    metrics: MetricsData;
    timeRange: string;
    vehicleClass: VehicleClass;
    externalTripCounts?: ExternalTripCountsData | null;
    citySettings?: Settings;
}

function MetricsCharts({
    metrics,
    timeRange,
    vehicleClass,
    externalTripCounts,
    citySettings,
}: MetricsChartsProps) {
    const displayTimeRange = useDisplayTimeRange(
        metrics,
        timeRange,
        externalTripCounts
    );
    const linearUnits = useLinearUnitsInfo();

    return (
        <div className={styles.chartsContainer}>
            {Object.entries(CHART_TYPES).map(
                ([chartType, { key, description, maximumFractionDigits }]) => {
                    return (
                        <div
                            key={chartType}
                            className={cx('shadow-8', styles.chartRow)}
                        >
                            <div className={styles.chartTitleRow}>
                                <h1>
                                    {composeName(
                                        chartType as ChartDataType,
                                        vehicleClass
                                    )}
                                </h1>
                                <p>{displayTimeRange}</p>
                            </div>
                            <div className={styles.highlightedMetricsRow}>
                                <HighlightedMetric
                                    value={convertHighlightedMetricValuesForDisplay(
                                        key,
                                        metrics[key].average,
                                        metrics[key].yUnit,
                                        linearUnits
                                    )}
                                    chartType={key}
                                    name="average"
                                    unit={highlightedMetricDisplayUnits(
                                        metrics[key].yUnit.split('_').join(' '),
                                        linearUnits
                                    )}
                                    maximumFractionDigits={
                                        maximumFractionDigits
                                    }
                                    externalTripCounts={externalTripCounts}
                                    citySettings={citySettings}
                                    dataBuckets={metrics[key].dataBuckets}
                                    metricType="average"
                                    timeRange={timeRange}
                                    vehicleClass={vehicleClass}
                                    totalCountForAvgCalc={
                                        metrics.totalTripCount
                                    }
                                />

                                <HighlightedMetric
                                    value={convertHighlightedMetricValuesForDisplay(
                                        key,
                                        metrics[key].dataBuckets.reduce(
                                            (max, { yValue }) =>
                                                Math.max(max, yValue),
                                            metrics[key].dataBuckets[0].yValue
                                        ),
                                        metrics[key].yUnit,
                                        linearUnits
                                    )}
                                    chartType={key}
                                    name="maximum"
                                    unit={highlightedMetricDisplayUnits(
                                        metrics[key].yUnit.split('_').join(' '),
                                        linearUnits
                                    )}
                                    maximumFractionDigits={
                                        maximumFractionDigits
                                    }
                                />

                                <HighlightedMetric
                                    value={convertHighlightedMetricValuesForDisplay(
                                        key,
                                        metrics[key].dataBuckets.reduce(
                                            (min, { yValue }) =>
                                                Math.min(min, yValue),
                                            metrics[key].dataBuckets[0].yValue
                                        ),
                                        metrics[key].yUnit,
                                        linearUnits
                                    )}
                                    chartType={key}
                                    name="minimum"
                                    unit={highlightedMetricDisplayUnits(
                                        metrics[key].yUnit.split('_').join(' '),
                                        linearUnits
                                    )}
                                    maximumFractionDigits={
                                        maximumFractionDigits
                                    }
                                />

                                <div className={styles.right}>
                                    {key === 'tripsPerDay' ||
                                    key === 'tripDistancePerDay' ? (
                                        <HighlightedMetric
                                            value={convertHighlightedMetricValuesForDisplay(
                                                key,
                                                key === 'tripsPerDay'
                                                    ? metrics.totalTripCount
                                                    : key ===
                                                      'tripDistancePerDay'
                                                    ? metrics.tripDistancePerDay
                                                          .sumTotal
                                                    : null,
                                                metrics[key].yUnit,
                                                linearUnits
                                            )}
                                            chartType={key}
                                            name="total"
                                            unit={highlightedMetricDisplayUnits(
                                                metrics[key].yUnit
                                                    .split('_')
                                                    .join(' '),
                                                linearUnits
                                            )}
                                            maximumFractionDigits={
                                                maximumFractionDigits
                                            }
                                            externalTripCounts={
                                                externalTripCounts
                                            }
                                            citySettings={citySettings}
                                            dataBuckets={
                                                metrics[key].dataBuckets
                                            }
                                            timeRange={timeRange}
                                            metricType="total"
                                            vehicleClass={vehicleClass}
                                        />
                                    ) : null}
                                </div>
                            </div>
                            {metrics[chartType].dataBuckets.length > 0 && (
                                <div className={styles.chartContainer}>
                                    <div className={styles.chartWrapperOuter}>
                                        <div
                                            className={styles.chartWrapperInner}
                                        >
                                            <MetricChart
                                                metrics={convertChartDataForDisplay(
                                                    chartType as ChartDataType,
                                                    [metrics],
                                                    linearUnits
                                                )}
                                                chartDataType={
                                                    chartType as ChartDataType
                                                }
                                                timeRange={timeRange}
                                            />
                                        </div>
                                    </div>
                                    <p>{description}</p>
                                </div>
                            )}
                            {metrics[chartType].dataBuckets.length == 0 && (
                                <div>No Data</div>
                            )}
                        </div>
                    );
                }
            )}
        </div>
    );
}

export default MetricsCharts;
